import { useState } from 'react';
import { normalizeAdministrativeMoney } from '@snaphire/contracts';
import type { Schema } from '../api/client';
import { ApiError } from '../api/session';
import { Button, Empty, Field, Icon, Search, Table } from '../ui';
import {
  AdminConfirm,
  AdminForm,
  AdminLink,
  AdminSelect,
  InfoRows,
  QueryState,
  moneyLabel,
  nullable,
  text,
  useAdminAction,
  useAdminNavigate,
  useAdminQuery,
  useAdministrative,
} from './common';
import { AdministrativeList } from './list-layout';

export function AdministrativeProviders({ id, operation }: { id?: string; operation?: string }) {
  const me = useAdministrative();
  const [search, setSearch] = useState('');
  const action = useAdminAction();
  const navigate = useAdminNavigate();
  const creating = id === 'new';
  const list = useAdminQuery<Schema<'AdminProviderPage'>>('/api/v1/administrative/providers');
  const detail = useAdminQuery<Schema<'AdminProvider'>>(
    '/api/v1/administrative/providers/{id}',
    { id: id || '' },
    {},
    Boolean(id && !creating),
  );
  const row = detail.data;
  const [remove, setRemove] = useState<Schema<'AdminProvider'> | null>(null);
  const [resolution, setResolution] = useState('TRANSFER');
  const [replacement, setReplacement] = useState('');
  const inaccessible = detail.error instanceof ApiError && [403, 404].includes(detail.error.status);
  if (id && !creating && (inaccessible || !row)) return <QueryState query={detail} />;
  if (creating || operation === 'edit')
    return (
      <>
        {!creating && <QueryState query={detail} />}
        <ProviderForm
          key={id}
          row={row}
          reload={async () => {
            const result = await detail.refetch();
            return result.error ? undefined : result.data;
          }}
        />
      </>
    );
  if (row)
    return (
      <>
        <QueryState query={detail} />
        <header className="admin-heading">
          <h1>{row.name}</h1>
          <div className="admin-toolbar">
            {row.allowedActions.includes('provider.edit') && (
              <>
                <AdminLink className="pill" path={`providers/${row.id}/edit`}>
                  Изменить
                </AdminLink>
                <Button onClick={() => setRemove(row)}>Удалить</Button>
              </>
            )}
          </div>
        </header>
        <section className="panel">
          <InfoRows
            rows={[
              [
                'Сайт',
                row.serviceUrl && (
                  <a href={row.serviceUrl} target="_blank" rel="noreferrer">
                    {row.serviceUrl}
                  </a>
                ),
              ],
              ['Логин', row.login],
              ['Комментарий', row.comment],
            ]}
          />
        </section>
        {row.balances && (
          <ProviderBalances
            row={row}
            reload={async () => {
              const result = await detail.refetch();
              return result.error ? undefined : result.data;
            }}
          />
        )}
        {row.cards && <ProviderCards row={row} />}
        <section className="panel">
          <h2>Связанные ресурсы</h2>
          <AdminLink path={`resources?providerAccountId=${row.id}`} className="pill">
            Открыть ресурсы провайдера
          </AdminLink>
        </section>
        {remove && (
          <AdminConfirm
            title="Удалить провайдера?"
            action={action}
            onClose={() => setRemove(null)}
            onConfirm={async () => {
              if (
                await action.run(
                  'DELETE',
                  '/api/v1/administrative/providers/{id}',
                  {
                    version: remove.version,
                    resolution,
                    ...(resolution === 'TRANSFER' ? { replacementId: replacement } : {}),
                  },
                  { id: remove.id },
                  'Провайдер удалён',
                )
              )
                await navigate('providers');
            }}
          >
            <p>Связанные ресурсы нужно перенести к другому провайдеру или в корзину.</p>
            <AdminSelect
              label="Действие"
              options={[
                { id: 'TRANSFER', name: 'Перенести ресурсы' },
                { id: 'TRASH', name: 'Переместить ресурсы в корзину' },
              ]}
              value={resolution}
              onChange={setResolution}
            />
            {resolution === 'TRANSFER' && (
              <AdminSelect
                label="Новый провайдер"
                options={me.providers.filter((item) => item.id !== row.id)}
                value={replacement}
                onChange={setReplacement}
                required
              />
            )}
          </AdminConfirm>
        )}
      </>
    );
  return (
    <AdministrativeList
      count={list.data?.items.length}
      className="admin-provider-list"
      toolbar={
        <>
          <Search value={search} onChange={setSearch} placeholder="Поиск провайдеров" />
          <span className="toolbar-spacer" />
          {me.allowedActions.includes('provider.edit') && (
            <AdminLink className="pill admin-add-action" path="providers/new">
              <Icon name="Plus" size={16} />
              Создать провайдера
            </AdminLink>
          )}
        </>
      }
    >
      <QueryState query={list} />
      <Table
        rows={(list.data?.items || []).filter((row) =>
          `${row.name} ${row.login || ''}`
            .toLocaleLowerCase('ru')
            .includes(search.toLocaleLowerCase('ru')),
        )}
        columns={[
          {
            key: 'name',
            label: 'Название',
            render: (item) => <AdminLink path={`providers/${item.id}`}>{item.name}</AdminLink>,
          },
          { key: 'serviceUrl', label: 'Сайт' },
          { key: 'login', label: 'Логин' },
          { key: 'comment', label: 'Комментарий' },
        ]}
      />
    </AdministrativeList>
  );
}
function providerDraft(row?: Schema<'AdminProvider'>) {
  return {
    name: row?.name || '',
    serviceUrl: row?.serviceUrl || '',
    login: row?.login || '',
    comment: row?.comment || '',
  };
}
function ProviderForm({
  row,
  reload,
}: {
  row?: Schema<'AdminProvider'>;
  reload: () => Promise<Schema<'AdminProvider'> | undefined>;
}) {
  const me = useAdministrative();
  const action = useAdminAction();
  const navigate = useAdminNavigate();
  const [snapshot, setSnapshot] = useState(row);
  const [fields, setFields] = useState(() => providerDraft(row));
  const [duplicates, setDuplicates] = useState<Schema<'AdminDuplicatePreview'> | null>(null);
  const [draft, setDraft] = useState<Schema<'AdminProviderCreate'> | null>(null);
  const [reloading, setReloading] = useState(false);
  if (!(row?.allowedActions || me.allowedActions).includes('provider.edit'))
    return <Empty title="Изменение провайдера недоступно" />;
  const save = async (body: Schema<'AdminProviderCreate'>) => {
    const result = await action.run<Schema<'AdminProvider'>>(
      snapshot ? 'PATCH' : 'POST',
      snapshot ? '/api/v1/administrative/providers/{id}' : '/api/v1/administrative/providers',
      { ...body, ...(snapshot ? { version: snapshot.version } : {}) },
      snapshot ? { id: snapshot.id } : {},
    );
    if (result) await navigate(`providers/${result.id}`);
  };
  return (
    <>
      <AdminForm
        title={snapshot ? `Изменить: ${snapshot.name}` : 'Создать провайдера'}
        action={{ ...action, pending: action.pending || reloading }}
        cancel={snapshot ? `providers/${snapshot.id}` : 'providers'}
        submit={async (data) => {
          const body = {
            name: text(data, 'name'),
            serviceUrl: nullable(data, 'serviceUrl'),
            login: nullable(data, 'login'),
            comment: nullable(data, 'comment'),
          };
          const preview = await action.run<Schema<'AdminDuplicatePreview'>>(
            'POST',
            '/api/v1/administrative/providers/duplicate-preview',
            body,
            {},
            '',
          );
          if (!preview) return;
          const matches = preview.duplicates.filter((item) => item.id !== snapshot?.id);
          if (matches.length) {
            setDuplicates({ ...preview, duplicates: matches });
            setDraft(body);
          } else await save(body);
        }}
      >
        {row &&
          (row.version !== snapshot?.version ||
            (action.error instanceof ApiError && action.error.status === 409)) && (
            <div role="status">
              <p>Другой сотрудник изменил запись. Ваши введённые значения сохранены.</p>
              <Button
                disabled={action.pending || reloading}
                onClick={async () => {
                  setReloading(true);
                  try {
                    const latest = await reload();
                    if (!latest) return;
                    setSnapshot(latest);
                    setFields(providerDraft(latest));
                    setDuplicates(null);
                    setDraft(null);
                    action.setError(null);
                  } finally {
                    setReloading(false);
                  }
                }}
              >
                Загрузить новую версию
              </Button>
            </div>
          )}
        <Field
          label="Название"
          name="name"
          required
          maxLength={200}
          value={fields.name}
          onChange={(event) => setFields({ ...fields, name: event.target.value })}
        />
        <Field
          label="Сайт сервиса"
          name="serviceUrl"
          type="url"
          value={fields.serviceUrl}
          onChange={(event) => setFields({ ...fields, serviceUrl: event.target.value })}
        />
        <Field
          label="Логин аккаунта у провайдера"
          name="login"
          autoComplete="off"
          value={fields.login}
          onChange={(event) => setFields({ ...fields, login: event.target.value })}
        />
        <Field label="Комментарий">
          <textarea
            name="comment"
            maxLength={10000}
            value={fields.comment}
            onChange={(event) => setFields({ ...fields, comment: event.target.value })}
          />
        </Field>
      </AdminForm>
      {duplicates && draft && (
        <AdminConfirm
          title="Возможные дубликаты"
          action={action}
          onClose={() => {
            setDuplicates(null);
            setDraft(null);
          }}
          onConfirm={() => save(draft)}
          label="Всё равно сохранить"
        >
          <p>Проверьте существующие аккаунты провайдера:</p>
          {duplicates.duplicates.map((item) => (
            <p key={item.id}>
              <AdminLink path={`providers/${item.id}`}>{item.name}</AdminLink>
            </p>
          ))}
        </AdminConfirm>
      )}
    </>
  );
}
function ProviderBalances({
  row,
  reload,
}: {
  row: Schema<'AdminProvider'>;
  reload: () => Promise<Schema<'AdminProvider'> | undefined>;
}) {
  const action = useAdminAction();
  const [currency, setCurrency] = useState('USD');
  const balance = row.balances?.find((item) => item.currency === currency);
  const [editing, setEditing] = useState(false);
  const [version, setVersion] = useState(0);
  const [amount, setAmount] = useState('');
  const [reloading, setReloading] = useState(false);
  return (
    <section className="panel">
      <h2>Баланс аккаунта</h2>
      <div className="admin-metrics">
        {['USD', 'EUR', 'RUB'].map((currency) => (
          <div key={currency}>
            <p className="muted">{currency}</p>
            <strong>
              {moneyLabel(
                row.balances?.find((item) => item.currency === currency)?.amount,
                currency,
              )}
            </strong>
          </div>
        ))}
      </div>
      {row.allowedActions.includes('finance.edit') && (
        <Button
          onClick={() => {
            setAmount(balance?.amount || '');
            setVersion(balance?.version || 0);
            setEditing(true);
          }}
        >
          Обновить баланс
        </Button>
      )}
      {editing && (
        <AdminConfirm
          title="Обновить баланс"
          action={{ ...action, pending: action.pending || reloading }}
          onClose={() => setEditing(false)}
          onConfirm={async () => {
            try {
              const value = normalizeAdministrativeMoney(amount, true);
              if (
                await action.run(
                  'POST',
                  '/api/v1/administrative/providers/{id}/balances',
                  { version, currency, amount: value },
                  { id: row.id },
                )
              )
                setEditing(false);
            } catch (error) {
              action.setError(error as Error);
            }
          }}
        >
          {((balance?.version || 0) !== version ||
            (action.error instanceof ApiError && action.error.status === 409)) && (
            <div role="status">
              <p>Другой сотрудник изменил запись. Ваши введённые значения сохранены.</p>
              <Button
                disabled={action.pending || reloading}
                onClick={async () => {
                  setReloading(true);
                  try {
                    const latest = await reload();
                    if (!latest) return;
                    const latestBalance = latest.balances?.find(
                      (item) => item.currency === currency,
                    );
                    setVersion(latestBalance?.version || 0);
                    setAmount(latestBalance?.amount || '');
                    action.setError(null);
                  } finally {
                    setReloading(false);
                  }
                }}
              >
                Загрузить новую версию
              </Button>
            </div>
          )}
          <AdminSelect
            label="Валюта"
            options={['USD', 'EUR', 'RUB'].map((id) => ({ id, name: id }))}
            value={currency}
            onChange={(value) => {
              setCurrency(value);
              setAmount(row.balances?.find((item) => item.currency === value)?.amount || '');
              setVersion(row.balances?.find((item) => item.currency === value)?.version || 0);
            }}
          />
          <Field
            label="Текущий баланс"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <p className="muted">Балансы хранятся отдельно по валютам.</p>
        </AdminConfirm>
      )}
    </section>
  );
}
function ProviderCards({ row }: { row: Schema<'AdminProvider'> }) {
  const action = useAdminAction();
  const [editing, setEditing] = useState<{
    id?: string;
    version?: number;
    name: string;
    last4: string;
    comment: string;
  } | null>(null);
  const [remove, setRemove] = useState<
    NonNullable<Schema<'AdminProvider'>['cards']>[number] | null
  >(null);
  return (
    <section className="panel">
      <header>
        <h2>Карты для оплаты</h2>
        {row.allowedActions.includes('finance.edit') && (
          <Button onClick={() => setEditing({ name: '', last4: '', comment: '' })}>
            Добавить карту
          </Button>
        )}
      </header>
      <Table
        rows={row.cards || []}
        columns={[
          { key: 'name', label: 'Название' },
          { key: 'last4', label: 'Последние 4 цифры', render: (card) => `•••• ${card.last4}` },
          { key: 'comment', label: 'Комментарий' },
          {
            key: 'action',
            label: '',
            sortable: false,
            filter: false,
            render: (card) =>
              row.allowedActions.includes('finance.edit') && (
                <div className="admin-toolbar">
                  <Button onClick={() => setEditing({ ...card, comment: card.comment || '' })}>
                    Изменить
                  </Button>
                  <Button onClick={() => setRemove(card)}>Удалить</Button>
                </div>
              ),
          },
        ]}
      />
      {editing && (
        <AdminConfirm
          title={editing.id ? 'Изменить карту' : 'Добавить карту'}
          action={action}
          onClose={() => setEditing(null)}
          onConfirm={async () => {
            if (!/^\d{4}$/.test(editing.last4) || !editing.name.trim()) {
              action.setError(new Error('Укажите название и последние четыре цифры'));
              return;
            }
            const body = {
              name: editing.name,
              last4: editing.last4,
              comment: editing.comment || null,
              ...(editing.id ? { version: editing.version } : {}),
            };
            if (
              await action.run(
                editing.id ? 'PATCH' : 'POST',
                editing.id
                  ? '/api/v1/administrative/providers/{id}/cards/{cardId}'
                  : '/api/v1/administrative/providers/{id}/cards',
                body,
                { id: row.id, ...(editing.id ? { cardId: editing.id } : {}) },
              )
            )
              setEditing(null);
          }}
        >
          <Field
            label="Название"
            value={editing.name}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          />
          <Field
            label="Последние четыре цифры"
            inputMode="numeric"
            maxLength={4}
            pattern="[0-9]{4}"
            value={editing.last4}
            onChange={(e) => setEditing({ ...editing, last4: e.target.value })}
          />
          <Field
            label="Комментарий"
            value={editing.comment}
            onChange={(e) => setEditing({ ...editing, comment: e.target.value })}
          />
          <p className="muted">Сохраняются только название и последние четыре цифры.</p>
        </AdminConfirm>
      )}
      {remove && (
        <AdminConfirm
          title="Удалить карту?"
          action={action}
          onClose={() => setRemove(null)}
          onConfirm={async () => {
            if (
              await action.run(
                'DELETE',
                '/api/v1/administrative/providers/{id}/cards/{cardId}',
                { version: remove.version },
                { id: row.id, cardId: remove.id },
                'Карта удалена',
              )
            )
              setRemove(null);
          }}
        >
          <p>
            {remove.name} · •••• {remove.last4}
          </p>
        </AdminConfirm>
      )}
    </section>
  );
}
